import { api, type Api } from "@api/bot-base-api-request";
import { useMutation } from "@tanstack/react-query";

/**
 * Mutation key for unpausing all copy trades
 */
export const BOT_UNPAUSE_ALL_COPY_TRADE_QUERY_KEY =
  "bot-unpause-all-copy-trade";

/**
 * Parameters for unpausing all copy trades
 * @typedef {Object} BotUnpauseAllCopyTradeParams
 * @property {string} userId - User's unique identifier
 * @property {string} walletAddress - User's wallet address
 * @property {number} chainId - ID of the blockchain network
 */
export type BotUnpauseAllCopyTradeParams = {
  userId: string;
  walletAddress: string;
  chainId: number;
  appPubKey?: any;
};

/**
 * Response structure for unpausing all copy trades
 * @typedef {Object} UpdateCopyTradeResponse
 * @property {string} message - Success or error message
 * @property {Array<Object>} copyTradeData - Updated array of wallets being copied
 */
export type UpdateCopyTradeResponse = {
  message: string;
  copyTradeData: Array<{
    address: string;
    nickname?: string;
    amountPercent?: number;
    isDuplicateBuy?: boolean;
    slippage?: number;
    tokenRenounced?: boolean;
    tokenMinLiquidity?: number;
    tokenMaxLiquidity?: number;
    tokenMinMC?: number;
    tokenMaxMC?: number;
    tokenMaxBuyTax?: number;
    tokenMaxSellTax?: number;
    copySell?: boolean;
    isOn?: boolean;
    useProfile?: boolean;
    createAt?: number;
    isBuyWithFixedAmount?: boolean;
    amount?: number;
    isAutoSlippage?: boolean;
  }>;
};

/**
 * Unpauses all copy trades for a user wallet
 *
 * @param {Api} api - API instance
 * @param {BotUnpauseAllCopyTradeParams} params - Parameters for the API request
 * @returns {Promise<UpdateCopyTradeResponse>} Promise that resolves to update response
 */
export const postBotUnpauseAllCopyTrade = async (
  api: Api,
  params: BotUnpauseAllCopyTradeParams
): Promise<UpdateCopyTradeResponse> =>
  api.post("copyTrade/unpauseAll", { json: params }).json();

/**
 * React Query mutation hook for unpausing all copy trades
 *
 * @returns {UseMutationResult<UpdateCopyTradeResponse, Error, BotUnpauseAllCopyTradeParams>} React Query mutation result
 *
 * @example
 * // Use mutation to unpause all copy trades
 * const unpauseAllMutation = useBotUnpauseAllCopyTradeMutation();
 *
 * // Later in your code:
 * unpauseAllMutation.mutate({
 *   userId: "user123",
 *   walletAddress: "0x123...abc",
 *   chainId: 1
 * });
 */
export const useBotUnpauseAllCopyTradeMutation = () =>
  useMutation({
    mutationFn: async (params: BotUnpauseAllCopyTradeParams) =>
      postBotUnpauseAllCopyTrade(api, params),
    mutationKey: [BOT_UNPAUSE_ALL_COPY_TRADE_QUERY_KEY],
  });
